import { Handle, Position } from 'reactflow';
import { Users, MessageSquare, Clock, FileText } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { WorkflowNodeData } from '@/types/workflow';
import type { WorkflowChannel } from '@/types';

function NodeCard({ selected, children, className }: { selected: boolean; children: React.ReactNode; className?: string }) {
  return (
    <div className={cn(
      "w-64 rounded-xl border bg-white shadow-sm select-none relative",
      selected ? "border-primary ring-1 ring-primary shadow-md" : "border-gray-200",
      className
    )}>
      {children}
    </div>
  );
}

function NodeLabels({ type, category }: { type: string; category: string }) {
  return (
    <div className="flex items-center justify-between px-3 pt-2.5 pb-1.5">
      <span className="text-[9px] font-semibold uppercase tracking-widest text-muted-foreground">{type}</span>
      <span className="text-[9px] font-semibold uppercase tracking-widest text-muted-foreground">{category}</span>
    </div>
  );
}

function NodeDivider() {
  return <div className="mx-3 border-t border-dashed border-gray-200" />;
}

function NodeContent({ icon, title, description }: { icon: React.ReactNode; title: string; description?: string }) {
  return (
    <div className="px-3 py-2.5">
      <div className="flex items-start gap-2.5">
        {icon}
        <div className="min-w-0">
          <p className="font-semibold text-sm leading-tight">{title}</p>
          {description && (
            <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{description}</p>
          )}
        </div>
      </div>
    </div>
  );
}

// Audience Node
export function AudienceNode({ data, selected }: { data: WorkflowNodeData; selected: boolean }) {
  const targetLabels: Record<string, string> = {
    all: 'Todos os contatos',
    tags: 'Por tags',
    segment: 'Segmento comportamental',
  };
  const label = targetLabels[data.targetType || 'all'];
  const segmentNames = (data.selectedSegments || []).map((s: any) => s.name).join(', ');
  const description = data.targetType === 'segment' && segmentNames ? segmentNames : label;

  return (
    <NodeCard selected={selected}>
      <NodeLabels type="Trigger" category="Audiência" />
      <NodeDivider />
      <NodeContent
        icon={<div className="p-1.5 rounded-lg bg-indigo-50 shrink-0"><Users className="w-4 h-4 text-indigo-600" /></div>}
        title="Audiência"
        description={description}
      />
      {(data.contactCount || 0) > 0 && (
        <div className="px-3 pb-2.5">
          <span className="inline-flex items-center rounded-full bg-indigo-50 px-2 py-0.5 text-[10px] font-medium text-indigo-700">
            {data.contactCount} contatos
          </span>
        </div>
      )}
      <Handle
        type="source"
        position={Position.Bottom}
        className="w-3! h-3! bg-indigo-500! border-2! border-white! shadow-sm!"
      />
    </NodeCard>
  );
}

// Message Node
export function MessageNode({ data, selected }: { data: WorkflowNodeData; selected: boolean }) {
  const isTemplate = data.messageMode === 'template';
  const hasPushFields = Boolean(data.pushTitle || data.pushDescription);

  const title = isTemplate
    ? (data.templateDisplayName || data.templateName || 'Selecione um template')
    : (data.pushTitle || 'Configurar push')

  const description = isTemplate
    ? (data.templateBodyText || undefined)
    : (data.pushDescription || undefined)

  return (
    <NodeCard selected={selected}>
      <Handle
        type="target"
        position={Position.Top}
        className="w-3! h-3! bg-blue-500! border-2! border-white! shadow-sm!"
      />
      <NodeLabels type="Ação" category={isTemplate ? 'Template' : 'Push'} />
      <NodeDivider />
      <NodeContent
        icon={
          isTemplate
            ? <div className="p-1.5 rounded-lg bg-emerald-50 shrink-0"><FileText className="w-4 h-4 text-emerald-600" /></div>
            : <div className="p-1.5 rounded-lg bg-blue-50 shrink-0"><MessageSquare className="w-4 h-4 text-blue-600" /></div>
        }
        title={title}
        description={description}
      />
      {!isTemplate && hasPushFields && (data.pushUrl || data.pushImageUrl) && (
        <div className="flex gap-1 px-3 pb-2.5">
          {data.pushUrl && (
            <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-600">URL</span>
          )}
          {data.pushImageUrl && (
            <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-600">Imagem</span>
          )}
        </div>
      )}
      <Handle
        type="source"
        position={Position.Bottom}
        className="w-3! h-3! bg-blue-500! border-2! border-white! shadow-sm!"
      />
    </NodeCard>
  );
}

export function FacebookMessageNode({ data, selected }: { data: WorkflowNodeData; selected: boolean }) {
  const templateName = data.templateName || 'Template não configurado';
  const templateParams = data.templateParams
    ?.split(/[\n,]/)
    .map((value: string) => value.trim())
    .filter(Boolean) || [];

  return (
    <NodeCard selected={selected}>
      <Handle
        type="target"
        position={Position.Top}
        className="w-3! h-3! bg-emerald-600! border-2! border-white! shadow-sm!"
      />
      <NodeLabels type="Ação" category="Messenger" />
      <NodeDivider />
      <NodeContent
        icon={<div className="p-1.5 rounded-lg bg-emerald-50 shrink-0"><MessageSquare className="w-4 h-4 text-emerald-600" /></div>}
        title={templateName}
        description={templateParams.length > 0 ? templateParams.slice(0, 2).join(', ') : undefined}
      />
      {data.messageTag && (
        <div className="px-3 pb-2.5">
          <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-600">{data.messageTag}</span>
        </div>
      )}
      <Handle
        type="source"
        position={Position.Bottom}
        className="w-3! h-3! bg-emerald-600! border-2! border-white! shadow-sm!"
      />
    </NodeCard>
  );
}

// Delay Node
export function DelayNode({ data, selected }: { data: WorkflowNodeData; selected: boolean }) {
  return (
    <NodeCard selected={selected}>
      <Handle
        type="target"
        position={Position.Top}
        className="w-3! h-3! bg-orange-500! border-2! border-white! shadow-sm!"
      />
      <NodeLabels type="Lógica" category="Aguardar" />
      <NodeDivider />
      <NodeContent
        icon={<div className="p-1.5 rounded-lg bg-orange-50 shrink-0"><Clock className="w-4 h-4 text-orange-500" /></div>}
        title={`Aguardar ${data.delay || '5'} ${data.unit || 'minutos'}`}
        description="Pausa antes do próximo passo"
      />
      <Handle
        type="source"
        position={Position.Bottom}
        className="w-3! h-3! bg-orange-500! border-2! border-white! shadow-sm!"
      />
    </NodeCard>
  );
}

export const broadcastNodeTypes = {
  audience: AudienceNode,
  message: MessageNode,
  facebook_message: FacebookMessageNode,
  delay: DelayNode,
};

export const broadcastNodeCategories = (channel: WorkflowChannel) => (
  channel === 'facebook_message'
    ? [
        { type: 'facebook_message', label: 'Messenger', description: 'Envia template do Messenger', icon: FileText, color: 'bg-emerald-600' },
        { type: 'delay', label: 'Aguardar', description: 'Espera tempo', icon: Clock, color: 'bg-orange-500' },
      ]
    : [
        { type: 'message', label: 'Push', description: 'Envia notificação push', icon: MessageSquare, color: 'bg-blue-500' },
        { type: 'delay', label: 'Aguardar', description: 'Espera tempo', icon: Clock, color: 'bg-orange-500' },
      ]
);
